import React, { useState } from 'react';
import {
  PencilSquareIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  TrashIcon,
  StarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarDaysIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { Counselor, User, UserRole } from '../../types';
import { t } from '../../utils/translations';
import { IconButton } from '../ui/Button';
import { actionIcon } from '../ui/actionIcons';
import {
  getRoleLabel,
  canEditUser,
  canDeactivateUser,
  canDeleteUser,
  canTransferSupremeLeader,
  findCounselorForUser,
} from './adminUtils';

interface AdminUserTableProps {
  users: User[];
  counselors: Counselor[];
  loading: boolean;
  currentUserId?: string;
  currentUserRole?: UserRole;
  canCreateUsers: boolean;
  isSupremeLeader: boolean;
  onEdit: (user: User) => void;
  onDeactivate: (userId: string) => void;
  onReactivate: (userId: string) => void;
  reactivatingUserId?: string | null;
  onDelete: (userId: string) => void;
  onTransferSupremeLeader: (userId: string) => void;
}

type UserRowMeta = {
  user: User;
  counselor: Counselor | undefined;
  editable: boolean;
  deactivatable: boolean;
  deletable: boolean;
  transferable: boolean;
  expanded: boolean;
  sexLabel: string | null;
  casesMetaLabel: string;
  statusLabel: string;
};

function buildUserRowMeta(
  user: User,
  counselors: Counselor[],
  expandedId: string | null,
  currentUserId: string | undefined,
  currentUserRole: UserRole | undefined,
  canCreateUsers: boolean,
  isSupremeLeader: boolean
): UserRowMeta {
  const counselor = findCounselorForUser(counselors, user.id);
  const activeCases = counselor?.activeCases ?? 0;
  const workloadLevel = counselor?.workloadLevel ?? 'low';
  const workloadLevelLabel =
    workloadLevel === 'high'
      ? t.admin.users.workloadHigh
      : workloadLevel === 'moderate'
        ? t.admin.users.workloadModerate
        : t.admin.users.workloadLow;
  const sexLabel =
    counselor?.sex === 'feminin'
      ? t.admin.users.sexFemale
      : counselor?.sex === 'masculin'
        ? t.admin.users.sexMale
        : null;

  return {
    user,
    counselor,
    editable: canEditUser(user, currentUserId, currentUserRole, isSupremeLeader),
    deactivatable: canDeactivateUser(
      user,
      currentUserId,
      currentUserRole,
      isSupremeLeader
    ),
    deletable: canDeleteUser(
      user,
      currentUserId,
      canCreateUsers,
      isSupremeLeader,
      currentUserRole
    ),
    transferable: canTransferSupremeLeader(user, currentUserId, isSupremeLeader),
    expanded: expandedId === user.id,
    sexLabel,
    casesMetaLabel: counselor
      ? t.admin.users.workloadCases
          .replace('{level}', workloadLevelLabel)
          .replace('{count}', String(activeCases))
      : t.admin.users.noProfile,
    statusLabel: user.isActive ? t.admin.users.active : t.admin.users.inactive,
  };
}

type ExpandActionsProps = {
  meta: UserRowMeta;
  reactivatingUserId: string | null;
  onEdit: (user: User) => void;
  onDeactivate: (userId: string) => void;
  onReactivate: (userId: string) => void;
  onDelete: (userId: string) => void;
  onTransferSupremeLeader: (userId: string) => void;
};

const ExpandActions: React.FC<ExpandActionsProps> = ({
  meta,
  reactivatingUserId,
  onEdit,
  onDeactivate,
  onReactivate,
  onDelete,
  onTransferSupremeLeader,
}) => {
  const { user, editable, deactivatable, deletable, transferable } = meta;

  const deactivateOrReactivate = user.isActive ? (
    <button
      type="button"
      onClick={() => onDeactivate(user.id)}
      disabled={!deactivatable}
      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs font-medium text-amber-800 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none"
    >
      <NoSymbolIcon className="h-4 w-4" />
      {t.admin.users.deactivateUser}
    </button>
  ) : (
    <button
      type="button"
      onClick={() => onReactivate(user.id)}
      disabled={!deactivatable || reactivatingUserId === user.id}
      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-green-200 bg-white px-3 py-2 text-xs font-medium text-green-800 transition hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none"
    >
      <CheckCircleIcon className="h-4 w-4" />
      {t.admin.users.reactivateUser}
    </button>
  );

  const deleteBtn = deletable ? (
    <button
      type="button"
      onClick={() => onDelete(user.id)}
      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-700 transition hover:bg-red-50 sm:flex-none"
    >
      <TrashIcon className="h-4 w-4" />
      {t.common.delete}
    </button>
  ) : null;

  const transferBtn = transferable ? (
    <button
      type="button"
      onClick={() => onTransferSupremeLeader(user.id)}
      className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs font-medium text-amber-800 transition hover:bg-amber-50 sm:w-auto"
    >
      <StarIcon className="h-4 w-4" />
      {t.admin.users.transferSupremeLeader}
    </button>
  ) : null;

  const editBtn = (
    <button
      type="button"
      onClick={() => onEdit(user)}
      disabled={!editable}
      className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2.5 text-xs font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
    >
      <PencilSquareIcon className="h-4 w-4" />
      {t.admin.users.editFields}
    </button>
  );

  return (
    <div className="space-y-2 border-t border-slate-100 bg-slate-50/70 px-4 py-3 sm:flex sm:flex-wrap sm:items-center sm:justify-end sm:gap-2 sm:space-y-0">
      <div className="sm:order-last">{editBtn}</div>
      <div className="flex gap-2 sm:contents">
        {deactivateOrReactivate}
        {deleteBtn}
      </div>
      {transferBtn}
    </div>
  );
};

const UserExpandPanel: React.FC<ExpandActionsProps> = ({
  meta,
  reactivatingUserId,
  onEdit,
  onDeactivate,
  onReactivate,
  onDelete,
  onTransferSupremeLeader,
}) => {
  const { user, counselor, sexLabel } = meta;

  return (
    <div className="border-t border-slate-100 bg-slate-50/40">
      <div className="border-b border-slate-100 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t.admin.users.profileDetails}
        </p>
      </div>

      {/* Compact stack on phone; two columns from sm */}
      <div className="space-y-3 p-4 sm:hidden">
        <div>
          <p className="text-[11px] text-slate-500">{t.admin.users.email}</p>
          <p className="break-all text-sm font-medium text-slate-800">{user.email}</p>
        </div>
        <div>
          <p className="text-[11px] text-slate-500">{t.admin.users.phoneNumber}</p>
          <p className="text-sm font-medium text-slate-800">{counselor?.phoneNumber || '—'}</p>
        </div>
        <div className="flex gap-4">
          <div className="flex-1">
            <p className="text-[11px] text-slate-500">{t.admin.users.sex}</p>
            <p className="text-sm font-medium text-slate-800">{sexLabel || '—'}</p>
          </div>
          <div className="flex-1">
            <p className="text-[11px] text-slate-500">{t.admin.users.birthDate}</p>
            <p className="text-sm font-medium text-slate-800">
              {counselor?.birthDate
                ? counselor.birthDate.toLocaleDateString('ro-RO')
                : '—'}
            </p>
          </div>
        </div>
        <div>
          <p className="mb-1.5 text-[11px] text-slate-500">{t.admin.users.specialties}</p>
          {counselor?.specialties?.length ? (
            <div className="flex flex-wrap gap-1.5">
              {counselor.specialties.map((spec) => (
                <span
                  key={spec}
                  className="inline-flex rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700"
                >
                  {spec}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">—</p>
          )}
        </div>
      </div>

      <div className="hidden gap-6 p-4 sm:grid sm:grid-cols-2">
        <div className="space-y-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {t.admin.users.contactInfo}
          </p>
          <div className="flex items-start gap-2.5">
            <EnvelopeIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <div className="min-w-0">
              <p className="text-[11px] text-slate-500">{t.admin.users.email}</p>
              <p className="truncate text-sm text-slate-800">{user.email}</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <PhoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <div>
              <p className="text-[11px] text-slate-500">{t.admin.users.phoneNumber}</p>
              <p className="text-sm text-slate-800">{counselor?.phoneNumber || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <CalendarDaysIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <div>
              <p className="text-[11px] text-slate-500">{t.admin.users.created}</p>
              <p className="text-sm text-slate-800">
                {user.createdAt.toLocaleDateString('ro-RO')}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {t.admin.users.personalInfo}
          </p>
          <div className="flex items-start gap-2.5">
            <UserIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <div>
              <p className="text-[11px] text-slate-500">{t.admin.users.sex}</p>
              <p className="text-sm text-slate-800">{sexLabel || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <CalendarDaysIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <div>
              <p className="text-[11px] text-slate-500">{t.admin.users.birthDate}</p>
              <p className="text-sm text-slate-800">
                {counselor?.birthDate
                  ? counselor.birthDate.toLocaleDateString('ro-RO')
                  : '—'}
              </p>
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-[11px] text-slate-500">{t.admin.users.specialties}</p>
            {counselor?.specialties?.length ? (
              <div className="flex flex-wrap gap-1.5">
                {counselor.specialties.map((spec) => (
                  <span
                    key={spec}
                    className="inline-flex rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700"
                  >
                    {spec}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">—</p>
            )}
          </div>
        </div>
      </div>

      <ExpandActions
        meta={meta}
        reactivatingUserId={reactivatingUserId}
        onEdit={onEdit}
        onDeactivate={onDeactivate}
        onReactivate={onReactivate}
        onDelete={onDelete}
        onTransferSupremeLeader={onTransferSupremeLeader}
      />
    </div>
  );
};

const UserCard: React.FC<{
  meta: UserRowMeta;
  reactivatingUserId: string | null;
  onToggleExpand: () => void;
  onEdit: (user: User) => void;
  onDeactivate: (userId: string) => void;
  onReactivate: (userId: string) => void;
  onDelete: (userId: string) => void;
  onTransferSupremeLeader: (userId: string) => void;
}> = ({
  meta,
  reactivatingUserId,
  onToggleExpand,
  onEdit,
  onDeactivate,
  onReactivate,
  onDelete,
  onTransferSupremeLeader,
}) => {
  const { user, editable, expanded, counselor, casesMetaLabel, statusLabel } = meta;

  return (
    <div
      className={`overflow-hidden rounded-xl border bg-white transition ${
        expanded
          ? 'border-slate-300 shadow-sm'
          : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <div className="flex items-center gap-2 px-3 py-3 sm:px-4 sm:py-3.5">
        <button
          type="button"
          onClick={onToggleExpand}
          className="min-w-0 flex-1 text-left"
          aria-expanded={expanded}
        >
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-slate-900 sm:text-[15px]">
              {user.fullName}
            </p>
            {user.isSupremeLeader && (
              <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                {t.admin.users.supremeLeaderBadge}
              </span>
            )}
          </div>
          <p className="mt-1.5 flex items-center overflow-hidden whitespace-nowrap text-xs text-slate-500 sm:text-[13px]">
            <span className="font-medium">{getRoleLabel(user.role)}</span>
            <span className="mx-1.5 text-slate-300">·</span>
            <span className={`font-semibold ${counselor ? 'text-slate-800' : 'text-slate-400'}`}>
              {casesMetaLabel}
            </span>
            <span className="mx-1.5 text-slate-300">·</span>
            <span className="font-medium">{statusLabel}</span>
          </p>
        </button>
        <div className="flex shrink-0 items-center gap-0.5">
          <IconButton
            icon={actionIcon.edit}
            label={t.common.edit}
            onClick={() => onEdit(user)}
            disabled={!editable}
          />
          <IconButton
            icon={expanded ? ChevronUpIcon : ChevronDownIcon}
            label={expanded ? t.admin.users.collapseDetails : t.admin.users.expandDetails}
            onClick={onToggleExpand}
            aria-expanded={expanded}
          />
        </div>
      </div>

      {expanded && (
        <UserExpandPanel
          meta={meta}
          reactivatingUserId={reactivatingUserId}
          onEdit={onEdit}
          onDeactivate={onDeactivate}
          onReactivate={onReactivate}
          onDelete={onDelete}
          onTransferSupremeLeader={onTransferSupremeLeader}
        />
      )}
    </div>
  );
};

const AdminUserTable: React.FC<AdminUserTableProps> = ({
  users,
  counselors,
  loading,
  currentUserId,
  currentUserRole,
  canCreateUsers,
  isSupremeLeader,
  onEdit,
  onDeactivate,
  onReactivate,
  reactivatingUserId = null,
  onDelete,
  onTransferSupremeLeader,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (loading) {
    return <div className="h-48 animate-pulse rounded-xl bg-slate-100" />;
  }

  return (
    <div className="space-y-3">
      {users.map((user) => {
        const meta = buildUserRowMeta(
          user,
          counselors,
          expandedId,
          currentUserId,
          currentUserRole,
          canCreateUsers,
          isSupremeLeader
        );
        return (
          <UserCard
            key={user.id}
            meta={meta}
            reactivatingUserId={reactivatingUserId}
            onEdit={onEdit}
            onDeactivate={onDeactivate}
            onReactivate={onReactivate}
            onDelete={onDelete}
            onTransferSupremeLeader={onTransferSupremeLeader}
            onToggleExpand={() => setExpandedId(meta.expanded ? null : user.id)}
          />
        );
      })}
    </div>
  );
};

export default AdminUserTable;
